/**
 * Growth data access. Snapshot freshness class throughout: refetch on focus, stale after
 * 15 minutes. Ad and analytics data has its own cadence on the server and every card
 * carries the age of the source it read, so a stale platform is visible rather than
 * silently old.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WindowKind } from '@elutide/shared';
import { apiFetch } from '@/lib/api';
import type {
  AcquisitionResponse,
  ChannelsResponse,
  EmailResponse,
  PartnerAttributionResponse,
  PartnersResponse,
  ReferralRow,
  ReferralsResponse,
} from './types';

export const growthKeys = {
  root: ['growth'] as const,
  acquisition: (window: WindowKind) => ['growth', 'acquisition', window] as const,
  channels: (window: WindowKind) => ['growth', 'channels', window] as const,
  email: (window: WindowKind) => ['growth', 'email', window] as const,
  referrals: (state: string) => ['growth', 'referrals', state] as const,
  partners: () => ['growth', 'partners'] as const,
  attribution: (partner: string | null) => ['growth', 'partners', 'attribution', partner] as const,
} as const;

const SNAPSHOT_STALE_MS = 15 * 60 * 1000;

/**
 * The referral queue's SLA block, recomputed from `entered_at`.
 *
 * The server builds `sla` by parsing a timestamp it read from WordPress, and when that
 * parse fails the block arrives as `{ hours: null, breached: false, at_risk: false }`:
 * every row reads "0h" and nothing is ever past the 48 hour SLA, which is the one thing
 * this queue exists to show. `entered_at` itself is a usable instant, so the age is
 * derived from it here and the server's block is kept only when it is sound.
 */
function withSla(row: ReferralRow, slaHours: number): ReferralRow {
  const enteredAt = String(row.entered_at).replace(' ', 'T');
  const entered = Date.parse(enteredAt.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(enteredAt) ? enteredAt : `${enteredAt}Z`);
  if (!Number.isFinite(entered)) return row;
  const hours = Math.max(0, (Date.now() - entered) / 3_600_000);
  return {
    ...row,
    entered_at: new Date(entered).toISOString(),
    sla: { hours, breached: hours > slaHours, at_risk: hours > slaHours * 0.75 },
  };
}

export function useAcquisition(window: WindowKind) {
  return useQuery({
    queryKey: growthKeys.acquisition(window),
    queryFn: () => apiFetch<AcquisitionResponse>('/growth/acquisition', { query: { window } }),
    staleTime: SNAPSHOT_STALE_MS,
    refetchOnWindowFocus: true,
  });
}

export function useChannels(window: WindowKind) {
  return useQuery({
    queryKey: growthKeys.channels(window),
    queryFn: () => apiFetch<ChannelsResponse>('/growth/channels', { query: { window, limit: 50 } }),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useEmailCampaigns(window: WindowKind) {
  return useQuery({
    queryKey: growthKeys.email(window),
    queryFn: () => apiFetch<EmailResponse>('/growth/email', { query: { window, limit: 50 } }),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useReferrals(state: 'queue' | 'decided' | 'all' = 'queue') {
  return useQuery({
    queryKey: growthKeys.referrals(state),
    queryFn: async () => {
      const page = await apiFetch<ReferralsResponse>('/growth/referrals', { query: { state, limit: 50 } });
      return { ...page, items: (page.items ?? []).map((row) => withSla(row, page.sla_hours)) };
    },
    // The review queue has a 48 hour SLA, so it belongs to the live class rather than the
    // snapshot one: a queue that is 15 minutes stale is a queue that misses its SLA.
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function usePartners() {
  return useQuery({
    queryKey: growthKeys.partners(),
    queryFn: () => apiFetch<PartnersResponse>('/growth/partners'),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function usePartnerAttribution(partner: string | null) {
  return useQuery({
    queryKey: growthKeys.attribution(partner),
    queryFn: () =>
      apiFetch<PartnerAttributionResponse>('/growth/partners/attribution', {
        query: { limit: 100, ...(partner ? { partner } : {}) },
      }),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

/**
 * Approve or deny a referral.
 *
 * Not optimistic. A reward is money leaving the building, and an optimistic approve that
 * the server then refuses would put a green tick on a decision that never happened.
 */
export function useReviewReferral() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; decision: 'approve' | 'deny'; reason: string; seen_score: number }) =>
      apiFetch<{ referral_id: number; decision: string; reward_cents: number }>(
        `/growth/referrals/${input.id}/review`,
        {
          method: 'POST',
          body: { decision: input.decision, reason: input.reason, seen_score: input.seen_score },
        },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: growthKeys.root });
    },
  });
}

export function useCreateAnnotation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { date: string; end_date: string | null; kind: string; text: string }) =>
      apiFetch<{ annotation_id: string }>('/growth/annotations', { method: 'POST', body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: growthKeys.root });
    },
  });
}
